import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  elementScroll,
  observeElementOffset,
  observeElementRect,
  VirtualItem,
  Virtualizer,
  VirtualizerOptions
} from '@tanstack/virtual-core';

/**
 * Lista virtualizada basada en `@tanstack/virtual-core` que utiliza un
 * contenedor de scroll EXTERNO (p. ej. el `ion-content`) como único scroll.
 *
 * El template del item se proyecta como `<ng-template>` y recibe el contexto:
 *   `$implicit` = item, `index` = índice, `last` = es el último.
 */
@Component({
  selector: 'eco-virtual-list',
  templateUrl: './eco-virtual-list.component.html',
  styleUrls: ['./eco-virtual-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EcoVirtualListComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** Colección a virtualizar. */
  @Input() items: unknown[] = [];
  /** Elemento con scroll (el scrollable del `ion-content`). */
  @Input() scrollElement: HTMLElement | null = null;
  /** Altura estimada por item (px). Los tamaños reales se miden en runtime. */
  @Input() estimatedSize = 252;
  /** Cantidad de items extra a renderizar fuera del viewport. */
  @Input() overscan = 3;
  /** Devuelve una key estable por item (mejora el trackBy y el cacheo de medidas). */
  @Input() getItemKey: (item: any, index: number) => string | number = (_item, index) => index;

  @ContentChild(TemplateRef) itemTemplate!: TemplateRef<unknown>;
  @ViewChild('spacer', { static: true }) spacerRef!: ElementRef<HTMLElement>;

  public virtualItems: VirtualItem[] = [];
  public totalSize = 0;
  public scrollMargin = 0;
  public virtualizer: Virtualizer<HTMLElement, HTMLElement> | null = null;

  private cleanupFn: (() => void) | null = null;
  private viewReady = false;
  private initScheduled = false;
  private settleRafId: number | null = null;
  private visibilityObserver: IntersectionObserver | null = null;
  private isListVisible = false;

  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.scheduleInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scrollElement']) {
      // Reinicializa cuando llega (o cambia) el contenedor de scroll.
      this.destroy();
      this.scheduleInit();
      return;
    }

    if (this.virtualizer && (changes['items'] || changes['estimatedSize'] || changes['overscan'])) {
      // Si la lista está oculta, aplazamos el refresco hasta que el observer la vuelva a ver.
      if (!this.isListVisible) return;

      // Detectamos si la cantidad de elementos realmente cambió
      const prevLength = changes['items']?.previousValue?.length;
      const currLength = changes['items']?.currentValue?.length;
      const lengthChanged = prevLength !== currLength;

      // Diferido para no mutar bindings dentro del CD del padre (evita NG0100).
      Promise.resolve().then(() => {
        if (!this.virtualizer) return;
        // Actualizamos las opciones (como el nuevo array o parámetros)
        this.applyOptions();
        // SOLO medimos desde cero si la cantidad de elementos cambió.
        // Si la cantidad es la misma (solo cambió de 0 a 1 un contador),
        // mantenemos el caché de alturas intacto para evitar el rebote.
        if (lengthChanged) {
          this.virtualizer.measure();
        }
        this.startMarginSettle();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private readonly onResize = () => {
    if (this.virtualizer) {
      // Limpia el histórico de alturas guardadas para obligar a TanStack a volver a ejecutar la directiva [ecoMeasure] con el nuevo DPI
      this.virtualizer.measure();
    }
    this.startMarginSettle();
  };

  public trackVirtual = (_: number, item: VirtualItem): string | number => item.key as string | number;

  /**
   * Difiere la creación del virtualizer a un microtask. Sus observers notifican
   * de forma síncrona al engancharse; hacerlo fuera del CD del padre evita el
   * error NG0100 (ExpressionChangedAfterItHasBeenChecked).
   */
  private scheduleInit(): void {
    if (!this.viewReady || !this.scrollElement || this.virtualizer || this.initScheduled) return;
    this.initScheduled = true;
    Promise.resolve().then(() => {
      this.initScheduled = false;
      this.init();
    });
  }

  /** Crea el virtualizer una vez que la vista y el scrollElement están listos. */
  private init(): void {
    if (!this.viewReady || !this.scrollElement || this.virtualizer) return;

    this.isListVisible = this.isLayoutVisible();

    // El virtualizer registra listeners de scroll/resize fuera de la zona de Angular
    this.zone.runOutsideAngular(() => {
      this.virtualizer = new Virtualizer<HTMLElement, HTMLElement>(this.buildOptions());
      this.cleanupFn = this.virtualizer._didMount();
      this.virtualizer._willUpdate();
      this.virtualizer.measure();
    });

    this.setupVisibilityObserver();

    if (this.isListVisible) {
      this.startMarginSettle();
    }

    window.addEventListener('resize', this.onResize, { passive: true });
  }

  /**
   * Recalcula el scrollMargin en cada frame hasta que se estabiliza.
   */
  private startMarginSettle(): void {
    if (!this.isListVisible) return;
    if (this.settleRafId != null) cancelAnimationFrame(this.settleRafId);
    const deadline = performance.now() + 1200;
    let lastMargin = Number.NaN;
    let stable = 0;
    const tick = () => {
      this.sync(); // Ejecuta recalculaciones y mediciones fuera de la zona
      if (this.scrollMargin === lastMargin) {
        stable++;
      } else {
        stable = 0;
        lastMargin = this.scrollMargin;
      }

      if (stable < 3 && performance.now() < deadline) {
        this.settleRafId = requestAnimationFrame(tick);
      } else {
        this.settleRafId = null;
      }
    };
    this.settleRafId = requestAnimationFrame(tick);
  }

  /** Aplica las opciones actuales y re-evalúa el scrollElement/observers. */
  private applyOptions(): void {
    if (!this.virtualizer) return;
    this.virtualizer!.setOptions(this.buildOptions());
    this.virtualizer!._willUpdate();
  }

  /**
   * Observa la visibilidad del spacer dentro del scrollElement.
   * Cuando la lista vuelve a ser visible (por ejemplo al regresar de otra
   * ruta en Ionic), fuerza una re-medición y re-sincronización.
   */
  private setupVisibilityObserver(): void {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    if (!this.scrollElement || !this.spacerRef) return;

    if (typeof IntersectionObserver === 'undefined') {
      this.isListVisible = true;
      return;
    }

    const root = this.scrollElement.contains(this.spacerRef.nativeElement)
      ? this.scrollElement
      : null;
    this.visibilityObserver = new IntersectionObserver(
      (entries) => this.onVisibilityChange(entries),
      {
        root,
        threshold: 0,
      }
    );
    this.visibilityObserver.observe(this.spacerRef.nativeElement);
  }

  private readonly onVisibilityChange = (entries: IntersectionObserverEntry[]): void => {
    this.applyOptions();
    this.virtualizer?.measure();
    this.resetScrollToTop();
    this.startMarginSettle();
  };

  /**
   * Comprueba de forma sincrónica si la lista y su contenedor de scroll
   * tienen caja de layout real (no están en un contenedor display:none).
   */
  private isLayoutVisible(): boolean {
    const spacer = this.spacerRef?.nativeElement;
    const scrollEl = this.scrollElement;
    if (!spacer || !scrollEl) return false;
    if (!spacer.isConnected || !scrollEl.isConnected) return false;
    if (spacer.hidden || scrollEl.hidden) return false;
    if (!spacer.offsetParent || !scrollEl.offsetParent) return false;

    const spacerRect = spacer.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    return spacerRect.width > 1 && scrollRect.width > 1;
  }

  /**
   * Reinicia el scroll al tope cuando la lista vuelve a ser visible
   * (por ejemplo al regresar de otra ruta en Ionic).
   */
  private resetScrollToTop(): void {
    if (!this.scrollElement) return;
    this.scrollElement.scrollTop = 0;
  }

  private destroy(): void {
    if (this.settleRafId != null) {
      cancelAnimationFrame(this.settleRafId);
      this.settleRafId = null;
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    window.removeEventListener('resize', this.onResize);
    this.cleanupFn?.();
    this.cleanupFn = null;
    this.virtualizer = null;
    this.isListVisible = false;
  }

  private buildOptions(): VirtualizerOptions<HTMLElement, HTMLElement> {
    return {
      count: this.items.length,
      getScrollElement: () => this.scrollElement,
      estimateSize: () => this.estimatedSize,
      scrollToFn: elementScroll,
      observeElementRect,
      observeElementOffset,
      overscan: this.overscan,
      scrollMargin: this.scrollMargin,
      enabled: this.isListVisible,
      getItemKey: (index) => this.getItemKey(this.items[index], index),
      onChange: () => this.onChange(),
    };
  }

  /** Callback del virtualizer: re-entra a la zona de Angular y refresca la vista. */
  private onChange(): void {
    this.zone.run(() => this.sync());
  }

  private sync(): void {
    if (!this.virtualizer || !this.isListVisible) return;
    // El margen puede variar si el contenido superior cambia de alto (banners,
    // toolbar de estado, etc). Se recalcula de forma barata y auto-corrige.
    this.recomputeScrollMargin();

    this.virtualItems = this.virtualizer.getVirtualItems();
    this.totalSize = this.virtualizer.getTotalSize();
    this.cdr.markForCheck();
  }

  /**
   * Calcula el desplazamiento de la lista dentro del contenido scrolleable.
   * Es la distancia entre el tope de la lista y el tope del contenido del
   * scroll, independientemente del scroll actual (se compensa con `scrollTop`).
   */
  private recomputeScrollMargin(): void {
    if (!this.scrollElement || !this.spacerRef || !this.virtualizer) return;
    const spacerTop = this.spacerRef.nativeElement.getBoundingClientRect().top;
    const scrollTop = this.scrollElement.getBoundingClientRect().top;
    const margin = Math.max(0, Math.round(spacerTop - scrollTop + this.scrollElement.scrollTop));
    if (Math.abs(margin - this.scrollMargin) > 1) {
      this.scrollMargin = margin;
      this.applyOptions();
    }
  }
}
